import React, { useMemo, useCallback, useState } from "react";
import { Button, Tag, Switch } from "@bikdotai/bik-component-library";
import { SidebarEnums } from "../../../app/growth-block/type";
import CommonTable from "../../../app/growth-block/common/ComonTable";
import AddOrEditFeatureModal from "../../../app/growth-block/common/AddOrEditFeatureModal";
import { WHATS_NEW_CONSTANTS } from "../constants/whatsNew.constants";

// Import hooks
import { useWhatsNewData } from "../hooks/useWhatsNewData";
import { useWhatsNewFilters } from "../hooks/useWhatsNewFilters";
import { useWhatsNewModals } from "../hooks/useWhatsNewModals";

// Import components
import { SearchInput } from "./SearchInput";
import { FilterButton } from "./FilterButton";
import { ModuleFilterTags } from "./ModuleFilterTags";
import { ActiveFiltersDisplay } from "./ActiveFiltersDisplay";
import { FilterModal } from "./FilterModal";
import { VideoModal } from "./VideoModal";
import BulkUploadModal from "./BulkUploadModal";
import { createWhatsNewTableRow } from "./WhatsNewTableRow";

const WhatsNewPanel: React.FC = () => {
  // Data management
  const {
    articles,
    loading,
    error,
    fetchArticles,
    updateArticleVisibility,
    archiveArticle,
  } = useWhatsNewData();

  // Filter management
  const {
    filters,
    filteredArticles,
    hasActiveFilters,
    activeFiltersInfo,
    updateSearchQuery,
    updateModuleFilter,
    updateUpdateTypeFilter,
    updateDateFilter,
    updateStatusFilter,
    updateCustomDateRange,
    clearAllFilters,
    moduleFilters,
    updateTypeFilters,
    dateFilters,
    statusFilters,
  } = useWhatsNewFilters(articles);

  // Modal management
  const {
    modalState,
    editingState,
    openFilterModal,
    closeFilterModal,
    showDatePicker,
    openVideoModal,
    closeVideoModal,
    openAddModal,
    openEditModal,
    closeEditModal,
  } = useWhatsNewModals();

  // Bulk upload modal
  const [isBulkUploadOpen, setIsBulkUploadOpen] = useState(false);

  // Event handlers
  const handleVisibilityToggle = useCallback(
    async (articleId: string, visibility: boolean) => {
      const article = articles.find((a) => a.docId === articleId);
      if (article) {
        await updateArticleVisibility(articleId, visibility);
      }
    },
    [articles, updateArticleVisibility]
  );

  const handleArchiveArticle = useCallback(
    async (articleId: string, shouldArchive: boolean) => {
      const article = articles.find((a) => a.docId === articleId);
      if (article) {
        await archiveArticle(articleId, shouldArchive);
      }
    },
    [articles, archiveArticle]
  );

  const handleEditArticle = useCallback(
    (articleId: string) => {
      const article = articles.find((a) => a.docId === articleId);
      if (article) {
        openEditModal(article);
      }
    },
    [articles, openEditModal]
  );

  const handlePlayVideo = useCallback((videoUrl: string) => {
    openVideoModal(videoUrl);
  }, [openVideoModal]);

  const formattedTableData = useMemo(() => {
    return filteredArticles.map((article) =>
      createWhatsNewTableRow({
        article,
        onEdit: () => handleEditArticle(article.docId),
        onArchive: () =>
          handleArchiveArticle(article.docId, !article.archived),
        onVisibilityToggle: (_, visibility) =>
          handleVisibilityToggle(article.docId, visibility),
        onPlayVideo: handlePlayVideo,
      })
    );
  }, [filteredArticles, handleEditArticle, handleArchiveArticle, handleVisibilityToggle, handlePlayVideo]);

  if (loading) {
    return <div>{WHATS_NEW_CONSTANTS.UI.LOADING_TEXT}</div>;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <>
      {/* Search and Filters Section */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "16px",
          marginBottom: "16px",
        }}
      >
        {/* Top Row: Search, Filters Button, Module Tags, Add Button */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "16px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              flex: 1,
            }}
          >
            <SearchInput
              value={filters.searchQuery}
              onChange={updateSearchQuery}
              placeholder={WHATS_NEW_CONSTANTS.UI.SEARCH_PLACEHOLDER}
            />

            <FilterButton onClick={openFilterModal} />

            <ModuleFilterTags
              filters={moduleFilters}
              selectedFilters={filters.selectedModuleFilter}
              onFilterChange={updateModuleFilter}
            />
          </div>

          <div style={{ display: "flex", gap: "8px" }}>
            <Button
              style={{ whiteSpace: "nowrap" }}
              buttonText="Bulk Upload"
              buttonType="secondary"
              onClick={() => setIsBulkUploadOpen(true)}
            />
            <Button
              style={{ whiteSpace: "nowrap" }}
              buttonText="+ Add new feature"
              onClick={openAddModal}
            />
          </div>
        </div>

        {/* Active Filters Row */}
        <ActiveFiltersDisplay
          hasActiveFilters={hasActiveFilters}
          activeFilters={activeFiltersInfo}
        />
      </div>

      {/* Table */}
      {filteredArticles.length === 0 ? (
        <div>{WHATS_NEW_CONSTANTS.UI.NO_RESULTS_TEXT}</div>
      ) : (
        <CommonTable
          tableHeadData={WHATS_NEW_CONSTANTS.TABLE_COLUMNS.map(
            (col) => col.label
          )}
          tableData={formattedTableData}
          appplyAnalyticsStyle={true}
          stickyActions={true}
          columnWidths={WHATS_NEW_CONSTANTS.TABLE_COLUMNS.reduce(
            (acc: Record<string, string>, col) => {
              acc[col.label] = col.width;
              return acc;
            },
            {} as Record<string, string>
          )}
          rowHeight={WHATS_NEW_CONSTANTS.UI.ROW_HEIGHT}
        />
      )}

      {/* Filter Modal */}
      <FilterModal
        isOpen={modalState.isFilterModalOpen}
        onClose={closeFilterModal}
        updateTypeFilters={updateTypeFilters}
        selectedUpdateTypeFilter={filters.selectedUpdateTypeFilter}
        onUpdateTypeChange={updateUpdateTypeFilter}
        dateFilters={dateFilters}
        selectedDateFilter={filters.selectedDateFilter}
        onDateFilterChange={updateDateFilter}
        statusFilters={statusFilters}
        selectedStatusFilter={filters.selectedStatusFilter}
        onStatusFilterChange={updateStatusFilter}
        showDatePicker={modalState.showDatePicker}
        onShowDatePicker={showDatePicker}
        customStartDate={filters.customStartDate}
        customEndDate={filters.customEndDate}
        onCustomDateChange={updateCustomDateRange}
        onClearAllFilters={clearAllFilters}
      />

      {/* Video Modal */}
      <VideoModal
        isOpen={modalState.videoModalOpen}
        videoUrl={modalState.currentVideoUrl}
        onClose={closeVideoModal}
      />

      {/* Bulk Upload Modal */}
      <BulkUploadModal
        isOpen={isBulkUploadOpen}
        onClose={() => setIsBulkUploadOpen(false)}
        onSuccess={fetchArticles}
        existingArticles={articles}
      />

      {/* Add/Edit Modal */}
      {editingState.openModal && (
        <AddOrEditFeatureModal
          isOpen={editingState.openModal}
          curentMenu={SidebarEnums["whats-new-section"]}
          onCloseModal={(reload) => {
            if (reload) {
              fetchArticles(); // Use our fetchArticles method instead of window.location.reload
            }
            closeEditModal();
          }}
          menuItemToEdit={
            editingState.operationType === "edit"
              ? editingState.itemSelectedForEditing
              : undefined
          }
          operationtype={editingState.operationType}
          indexToUpdate={editingState.indexToUpdate}
        />
      )}
    </>
  );
};

export default WhatsNewPanel;